import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { inventoryAPI, projectAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { 
  Building2, Home, Plus, Search, CheckCircle2, XCircle, Lock,
  Layers, IndianRupee, Ruler, Filter, LayoutGrid, List, Compass,
  Eye, EyeOff, ArrowUpRight, Settings, Info
} from 'lucide-react';

const InventoryPage = () => {
  const { hasRole } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const projectFromUrl = searchParams.get('project');
  const [units, setUnits] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(projectFromUrl || 'all');
  const [selectedBhk, setSelectedBhk] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedFacing, setSelectedFacing] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('card'); // 'card' or 'table'
  const [showUpgradeOptions, setShowUpgradeOptions] = useState(true); // Builder can toggle this
  const [selectedUnitForUpgrade, setSelectedUnitForUpgrade] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  
  // Check if user is builder admin
  const isBuilderAdmin = hasRole(['builder_admin']);
  const canManage = isBuilderAdmin;
  
  // For non-builders, check if they've applied filters
  const hasAppliedFilters = selectedProject !== 'all' || selectedBhk !== 'all' || selectedFacing !== 'all';

  const [newUnit, setNewUnit] = useState({
    project_id: '',
    unit_number: '',
    tower: '',
    floor: '',
    bhk_type: '',
    carpet_area: '',
    saleable_area: '',
    door_facing: 'north',
    price: '',
    // Other Costs
    parking_cost: '',
    clubhouse_cost: '',
    development_charges: '',
    other_charges: '',
  });

  const FACING_OPTIONS = [
    { value: 'north', label: 'North' },
    { value: 'south', label: 'South' },
    { value: 'east', label: 'East' },
    { value: 'west', label: 'West' },
    { value: 'north_east', label: 'North-East' },
    { value: 'north_west', label: 'North-West' },
    { value: 'south_east', label: 'South-East' },
    { value: 'south_west', label: 'South-West' },
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [unitsRes, projectsRes] = await Promise.all([
        inventoryAPI.list(),
        projectAPI.list(),
      ]);
      setUnits(unitsRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUnit = async (e) => {
    e.preventDefault();
    try {
      const carpetArea = newUnit.carpet_area ? parseFloat(newUnit.carpet_area) : null;
      const saleableArea = newUnit.saleable_area ? parseFloat(newUnit.saleable_area) : (carpetArea ? Math.round(carpetArea * 1.25) : null);
      
      await inventoryAPI.create({
        ...newUnit,
        floor: newUnit.floor ? parseInt(newUnit.floor) : null,
        carpet_area: carpetArea,
        saleable_area: saleableArea,
        price: newUnit.price ? parseFloat(newUnit.price) : null,
        parking_cost: newUnit.parking_cost ? parseFloat(newUnit.parking_cost) : null,
        clubhouse_cost: newUnit.clubhouse_cost ? parseFloat(newUnit.clubhouse_cost) : null,
        development_charges: newUnit.development_charges ? parseFloat(newUnit.development_charges) : null,
        other_charges: newUnit.other_charges ? parseFloat(newUnit.other_charges) : null,
      });
      toast.success('Unit added to inventory');
      setDialogOpen(false);
      setNewUnit({ 
        project_id: '', unit_number: '', tower: '', floor: '', bhk_type: '', 
        carpet_area: '', saleable_area: '', door_facing: 'north', price: '',
        parking_cost: '', clubhouse_cost: '', development_charges: '', other_charges: ''
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add unit');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      'available': { class: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2, label: 'Available' },
      'blocked': { class: 'bg-amber-100 text-amber-700', icon: Lock, label: 'Blocked' },
      'sold': { class: 'bg-red-100 text-red-700', icon: XCircle, label: 'Sold' },
    };
    const style = styles[status] || styles.available;
    const Icon = style.icon;
    return (
      <Badge className={style.class}>
        <Icon className="h-3 w-3 mr-1" />
        {style.label}
      </Badge>
    );
  };

  const formatPrice = (price) => {
    if (!price) return 'Price on request';
    if (price >= 10000000) return `₹${(price / 10000000).toFixed(2)} Cr`;
    if (price >= 100000) return `₹${(price / 100000).toFixed(2)} L`;
    return `₹${price.toLocaleString()}`;
  };

  const getProjectName = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    return project ? project.name : 'Unknown';
  };

  // Get unique BHK types
  const bhkTypes = [...new Set(units.map(u => u.bhk_type).filter(Boolean))];
  
  // Get series info for each BHK type (which flat numbers end in what)
  const getSeriesForBhk = (bhkType) => {
    const bhkUnits = units.filter(u => u.bhk_type === bhkType);
    const series = [...new Set(bhkUnits.map(u => {
      const unitNum = u.unit_number?.toString() || '';
      // Get the last 2 digits for series (e.g., 101 -> 01, 203 -> 03)
      return unitNum.slice(-2);
    }))].filter(s => s).sort();
    return series;
  };
  
  // Get BHK type with series info for display
  const getBhkWithSeries = (bhkType) => {
    const series = getSeriesForBhk(bhkType);
    if (series.length > 0 && series.length <= 4) {
      return `${bhkType} (${series.join(', ')})`;
    }
    return bhkType;
  };
  
  // Get unique facings
  const facingTypes = [...new Set(units.map(u => u.door_facing).filter(Boolean))];

  const filteredUnits = units.filter((u) => {
    const matchesProject = selectedProject === 'all' || u.project_id === selectedProject;
    const matchesBhk = selectedBhk === 'all' || u.bhk_type === selectedBhk;
    const matchesStatus = selectedStatus === 'all' || u.status === selectedStatus;
    const matchesFacing = selectedFacing === 'all' || u.door_facing === selectedFacing;
    const matchesSearch = u.unit_number?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // For non-builders, only show available units
    const statusFilter = isBuilderAdmin ? true : u.status === 'available';
    
    return matchesProject && matchesBhk && matchesStatus && matchesFacing && matchesSearch && statusFilter;
  });
  
  // Get upgrade options for a selected unit (other flats on same floor)
  const getUpgradeOptions = (unit) => {
    if (!unit || !showUpgradeOptions) return [];
    return filteredUnits.filter(u => 
      u.floor === unit.floor && 
      u.id !== unit.id && 
      u.status === 'available'
    );
  };

  const stats = {
    total: units.length,
    available: units.filter(u => u.status === 'available').length,
    blocked: units.filter(u => u.status === 'blocked').length,
    sold: units.filter(u => u.status === 'sold').length,
  };

  // Group by floor for floor plan view
  const floorGroups = filteredUnits.reduce((acc, unit) => {
    const floor = unit.floor || 0;
    if (!acc[floor]) acc[floor] = [];
    acc[floor].push(unit);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="inventory-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">
            {isBuilderAdmin ? 'Inventory Management' : 'Search Available Units'}
          </h1>
          <p className="text-muted-foreground">
            {isBuilderAdmin ? 'Real-time unit availability and pricing' : 'Find your perfect home'}
          </p>
        </div>
        
        <div className="flex gap-2">
          {/* Settings for Builder Admin */}
          {isBuilderAdmin && (
            <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="icon">
                  <Settings className="h-4 w-4" />
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Inventory Settings</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Show Upgrade Options</p>
                      <p className="text-sm text-muted-foreground">
                        When sales team views a unit, show other available flats on the same floor
                      </p>
                    </div>
                    <Switch 
                      checked={showUpgradeOptions} 
                      onCheckedChange={setShowUpgradeOptions}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={() => setSettingsOpen(false)}>Done</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          
          {canManage && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="btn-add-unit">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Unit
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Add Inventory Unit</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateUnit} className="space-y-4 mt-4">
                  {/* Project & Tower */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Project *</Label>
                      <Select value={newUnit.project_id} onValueChange={(v) => setNewUnit({...newUnit, project_id: v})} required>
                        <SelectTrigger><SelectValue placeholder="Select project" /></SelectTrigger>
                        <SelectContent>
                          {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Tower</Label>
                      <Input value={newUnit.tower} onChange={(e) => setNewUnit({...newUnit, tower: e.target.value})} placeholder="e.g., Tower A" />
                    </div>
                  </div>
                  
                  {/* Unit Number & Floor */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Unit Number *</Label>
                      <Input value={newUnit.unit_number} onChange={(e) => setNewUnit({...newUnit, unit_number: e.target.value})} required placeholder="e.g., A-1001" />
                    </div>
                    <div className="space-y-2">
                      <Label>Floor</Label>
                      <Input type="number" value={newUnit.floor} onChange={(e) => setNewUnit({...newUnit, floor: e.target.value})} placeholder="e.g., 10" />
                    </div>
                  </div>
                  
                  {/* BHK Type & Door Facing */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>BHK Type *</Label>
                    <Select value={newUnit.bhk_type} onValueChange={(v) => setNewUnit({...newUnit, bhk_type: v})} required>
                      <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1 BHK">1 BHK</SelectItem>
                        <SelectItem value="1.5 BHK">1.5 BHK</SelectItem>
                        <SelectItem value="2 BHK">2 BHK</SelectItem>
                        <SelectItem value="2.5 BHK">2.5 BHK</SelectItem>
                        <SelectItem value="3 BHK">3 BHK</SelectItem>
                        <SelectItem value="3.5 BHK">3.5 BHK</SelectItem>
                        <SelectItem value="4 BHK">4 BHK</SelectItem>
                        <SelectItem value="5 BHK">5 BHK</SelectItem>
                        <SelectItem value="Studio">Studio</SelectItem>
                        <SelectItem value="Penthouse">Penthouse</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Door Facing</Label>
                    <Select value={newUnit.door_facing} onValueChange={(v) => setNewUnit({...newUnit, door_facing: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {FACING_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Area - Carpet & Saleable */}
                <div className="p-3 bg-muted/30 rounded-lg space-y-3">
                  <p className="text-sm font-semibold flex items-center gap-2">
                    <Ruler className="h-4 w-4" />
                    Area Details
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Carpet Area (sq.ft) *</Label>
                      <Input type="number" value={newUnit.carpet_area} onChange={(e) => setNewUnit({...newUnit, carpet_area: e.target.value})} placeholder="e.g., 1000" required />
                    </div>
                    <div className="space-y-2">
                      <Label>Saleable Area (sq.ft)</Label>
                      <Input type="number" value={newUnit.saleable_area} onChange={(e) => setNewUnit({...newUnit, saleable_area: e.target.value})} placeholder="Auto: Carpet × 1.25" />
                    </div>
                  </div>
                </div>
                
                {/* Pricing */}
                <div className="p-3 bg-muted/30 rounded-lg space-y-3">
                  <p className="text-sm font-semibold flex items-center gap-2">
                    <IndianRupee className="h-4 w-4" />
                    Pricing
                  </p>
                  <div className="space-y-2">
                    <Label>Base Price (₹)</Label>
                    <Input type="number" value={newUnit.price} onChange={(e) => setNewUnit({...newUnit, price: e.target.value})} placeholder="e.g., 7500000" />
                  </div>
                </div>
                
                {/* Other Costs */}
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-3">
                  <p className="text-sm font-semibold flex items-center gap-2 text-amber-800">
                    <Plus className="h-4 w-4" />
                    Other Costs (Optional)
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm">Parking (₹)</Label>
                      <Input type="number" value={newUnit.parking_cost} onChange={(e) => setNewUnit({...newUnit, parking_cost: e.target.value})} placeholder="e.g., 500000" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">Clubhouse (₹)</Label>
                      <Input type="number" value={newUnit.clubhouse_cost} onChange={(e) => setNewUnit({...newUnit, clubhouse_cost: e.target.value})} placeholder="e.g., 100000" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm">Development Charges (₹)</Label>
                      <Input type="number" value={newUnit.development_charges} onChange={(e) => setNewUnit({...newUnit, development_charges: e.target.value})} placeholder="e.g., 200000" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">Other Charges (₹)</Label>
                      <Input type="number" value={newUnit.other_charges} onChange={(e) => setNewUnit({...newUnit, other_charges: e.target.value})} placeholder="e.g., 50000" />
                    </div>
                  </div>
                </div>
                
                <Button type="submit" className="w-full">Add Unit</Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
        </div>
      </div>

      {/* Stats - Only for Builder Admin */}
      {isBuilderAdmin && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="summary-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Layers className="h-4 w-4" />
                <span className="text-sm">Total Units</span>
              </div>
              <p className="text-2xl font-bold">{stats.total}</p>
            </CardContent>
          </Card>
          <Card className="summary-card border-emerald-200 bg-emerald-50/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-emerald-600 mb-1">
                <CheckCircle2 className="h-4 w-4" />
                <span className="text-sm">Available</span>
              </div>
              <p className="text-2xl font-bold text-emerald-700">{stats.available}</p>
            </CardContent>
          </Card>
          <Card className="summary-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-amber-600 mb-1">
                <Lock className="h-4 w-4" />
                <span className="text-sm">Blocked</span>
              </div>
              <p className="text-2xl font-bold">{stats.blocked}</p>
            </CardContent>
          </Card>
          <Card className="summary-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-red-600 mb-1">
                <XCircle className="h-4 w-4" />
                <span className="text-sm">Sold</span>
              </div>
              <p className="text-2xl font-bold">{stats.sold}</p>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Search Prompt for Non-Builders */}
      {!isBuilderAdmin && !hasAppliedFilters && (
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="py-8 text-center">
            <Search className="h-12 w-12 text-blue-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-blue-900 mb-2">Find Your Perfect Home</h3>
            <p className="text-blue-700 mb-4">Use the filters below to search for available units</p>
            <div className="flex justify-center gap-2 text-sm text-blue-600">
              <Badge variant="outline" className="border-blue-300">Select Project</Badge>
              <Badge variant="outline" className="border-blue-300">Choose BHK Type</Badge>
              <Badge variant="outline" className="border-blue-300">Pick Direction</Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search unit number..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
        </div>
        <Select value={selectedProject} onValueChange={setSelectedProject}>
          <SelectTrigger className="w-[180px]">
            <Building2 className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Project" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Projects</SelectItem>
            {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={selectedBhk} onValueChange={setSelectedBhk}>
          <SelectTrigger className="w-[200px]">
            <Home className="h-4 w-4 mr-2" />
            <SelectValue placeholder="BHK" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All BHK</SelectItem>
            {bhkTypes.map((bhk) => (
              <SelectItem key={bhk} value={bhk}>
                {getBhkWithSeries(bhk)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        {/* Facing Filter */}
        <Select value={selectedFacing} onValueChange={setSelectedFacing}>
          <SelectTrigger className="w-[160px]">
            <Compass className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Facing" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Facing</SelectItem>
            {FACING_OPTIONS.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        {/* Status Filter - Only for Builder Admin */}
        {isBuilderAdmin && (
          <Select value={selectedStatus} onValueChange={setSelectedStatus}>
            <SelectTrigger className="w-[150px]">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="available">Available</SelectItem>
              <SelectItem value="blocked">Blocked</SelectItem>
              <SelectItem value="sold">Sold</SelectItem>
            </SelectContent>
          </Select>
        )}
        
        {/* View Mode Toggle */}
        <div className="flex items-center gap-1 border rounded-lg p-1 ml-auto">
          <Button 
            variant={viewMode === 'compact' ? 'default' : 'ghost'} 
            size="sm"
            onClick={() => setViewMode('compact')}
            className="h-8"
            title="Compact view with flat numbers"
          >
            <Layers className="h-4 w-4 mr-1" />
            Compact
          </Button>
          <Button 
            variant={viewMode === 'card' ? 'default' : 'ghost'} 
            size="sm"
            onClick={() => setViewMode('card')}
            className="h-8"
          >
            <LayoutGrid className="h-4 w-4 mr-1" />
            Cards
          </Button>
          <Button 
            variant={viewMode === 'table' ? 'default' : 'ghost'} 
            size="sm"
            onClick={() => setViewMode('table')}
            className="h-8"
          >
            <List className="h-4 w-4 mr-1" />
            Table
          </Button>
        </div>
      </div>
      
      {/* Series Info Banner when BHK is selected */}
      {selectedBhk !== 'all' && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="py-3 px-4 flex items-center gap-3">
            <Home className="h-5 w-5 text-blue-600" />
            <div>
              <span className="font-medium text-blue-900">{selectedBhk} Units</span>
              <span className="text-blue-700 ml-2">
                Series: {getSeriesForBhk(selectedBhk).join(', ') || 'N/A'}
              </span>
              <span className="text-blue-600 ml-3 text-sm">
                ({filteredUnits.length} units)
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Floor-wise View */}
      {/* Show content based on role and filters */}
      {!isBuilderAdmin && !hasAppliedFilters ? (
        // Non-builders must apply filters first
        null
      ) : Object.keys(floorGroups).length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Building2 className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium">No units found</p>
            <p className="text-muted-foreground">
              {isBuilderAdmin ? 'Add inventory units to track availability' : 'Try adjusting your filters'}
            </p>
          </CardContent>
        </Card>
      ) : viewMode === 'table' ? (
        /* TABLE VIEW */
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center justify-between">
              <span className="flex items-center gap-2">
                <List className="h-5 w-5" />
                {isBuilderAdmin ? 'All Units' : 'Available Units'}
              </span>
              <Badge variant="outline">{filteredUnits.length} units</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-3 font-semibold">Floor</th>
                    <th className="text-left p-3 font-semibold">Flat No</th>
                    <th className="text-left p-3 font-semibold">Type</th>
                    <th className="text-right p-3 font-semibold">Carpet (sq.ft)</th>
                    <th className="text-right p-3 font-semibold">Saleable (sq.ft)</th>
                    <th className="text-left p-3 font-semibold">Facing</th>
                    <th className="text-right p-3 font-semibold">Price</th>
                    {isBuilderAdmin && <th className="text-center p-3 font-semibold">Status</th>}
                    {!isBuilderAdmin && showUpgradeOptions && <th className="text-center p-3 font-semibold">Upgrade</th>}
                  </tr>
                </thead>
                <tbody>
                  {filteredUnits
                    .sort((a, b) => (b.floor || 0) - (a.floor || 0) || a.unit_number?.localeCompare(b.unit_number))
                    .map((unit, index) => (
                    <tr 
                      key={unit.id} 
                      className={`border-b hover:bg-muted/30 cursor-pointer ${index % 2 === 0 ? '' : 'bg-muted/10'} ${selectedUnitForUpgrade?.id === unit.id ? 'bg-blue-50' : ''}`}
                      onClick={() => setSelectedUnitForUpgrade(unit)}
                    >
                      <td className="p-3">{unit.floor || '-'}</td>
                      <td className="p-3 font-medium">{unit.unit_number}</td>
                      <td className="p-3">{unit.bhk_type || '-'}</td>
                      <td className="p-3 text-right">{unit.carpet_area?.toLocaleString() || '-'}</td>
                      <td className="p-3 text-right">{unit.saleable_area?.toLocaleString() || '-'}</td>
                      <td className="p-3 capitalize">{unit.door_facing?.replace('_', '-') || '-'}</td>
                      <td className="p-3 text-right font-medium">{formatPrice(unit.price)}</td>
                      {isBuilderAdmin && <td className="p-3 text-center">{getStatusBadge(unit.status)}</td>}
                      {!isBuilderAdmin && showUpgradeOptions && (
                        <td className="p-3 text-center">
                          {getUpgradeOptions(unit).length > 0 && (
                            <Badge variant="outline" className="text-xs">
                              {getUpgradeOptions(unit).length} more on floor
                            </Badge>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Upgrade Options Panel */}
            {!isBuilderAdmin && showUpgradeOptions && selectedUnitForUpgrade && getUpgradeOptions(selectedUnitForUpgrade).length > 0 && (
              <div className="mt-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <ArrowUpRight className="h-5 w-5 text-green-600" />
                  <span className="font-semibold text-green-800">
                    Other Available Flats on Floor {selectedUnitForUpgrade.floor}
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {getUpgradeOptions(selectedUnitForUpgrade).map(u => (
                    <div key={u.id} className="p-3 bg-white rounded-lg border">
                      <div className="flex justify-between items-start mb-2">
                        <span className="font-bold">{u.unit_number}</span>
                        <Badge className="bg-green-500">{u.bhk_type}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{u.carpet_area} sq.ft • {u.door_facing?.replace('_', '-')}</p>
                      <p className="text-lg font-bold text-green-600 mt-1">{formatPrice(u.price)}</p>
                      {u.price && selectedUnitForUpgrade.price && (
                        <p className="text-xs text-muted-foreground">
                          {u.price > selectedUnitForUpgrade.price 
                            ? `+${formatPrice(u.price - selectedUnitForUpgrade.price)} more`
                            : u.price < selectedUnitForUpgrade.price 
                            ? `${formatPrice(selectedUnitForUpgrade.price - u.price)} less`
                            : 'Same price'}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ) : viewMode === 'card' ? (
        /* CARD VIEW */
        <div className="space-y-6">
          {Object.keys(floorGroups).sort((a, b) => b - a).map((floor) => (
            <Card key={floor}>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Layers className="h-5 w-5" />
                  Floor {floor}
                  <Badge variant="outline">{floorGroups[floor].length} units</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {floorGroups[floor].map((unit) => (
                    <div 
                      key={unit.id} 
                      onClick={() => setSelectedUnitForUpgrade(unit)}
                      className={`p-3 rounded-lg border-2 transition-all cursor-pointer hover:shadow-md ${
                        selectedUnitForUpgrade?.id === unit.id ? 'ring-2 ring-blue-500' : ''
                      } ${
                        unit.status === 'available' 
                          ? 'border-emerald-200 bg-emerald-50/50 hover:border-emerald-400' 
                          : unit.status === 'blocked'
                          ? 'border-amber-200 bg-amber-50/50'
                          : 'border-red-200 bg-red-50/50 opacity-75'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-bold text-sm">{unit.unit_number}</span>
                        {unit.status === 'available' && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                        {unit.status === 'blocked' && <Lock className="h-4 w-4 text-amber-600" />}
                        {unit.status === 'sold' && <XCircle className="h-4 w-4 text-red-600" />}
                      </div>
                      <p className="text-xs text-muted-foreground mb-1">{unit.bhk_type}</p>
                      {unit.carpet_area && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Ruler className="h-3 w-3" />
                          {unit.carpet_area} sq.ft
                        </p>
                      )}
                      {unit.door_facing && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1 capitalize">
                          <Compass className="h-3 w-3" />
                          {unit.door_facing.replace('_', '-')}
                        </p>
                      )}
                      <p className="text-sm font-semibold mt-2 flex items-center">
                        <IndianRupee className="h-3 w-3" />
                        {unit.price ? formatPrice(unit.price).replace('₹', '') : 'TBD'}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : viewMode === 'compact' ? (
        /* COMPACT VIEW - Flat numbers in rows with details below */
        <div className="space-y-4">
          {Object.keys(floorGroups).sort((a, b) => b - a).map((floor) => (
            <Card key={floor}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Layers className="h-4 w-4" />
                  Floor {floor}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {/* Flat Numbers Row */}
                <div className="flex flex-wrap gap-2 mb-3">
                  {floorGroups[floor].map((unit) => (
                    <button
                      key={unit.id}
                      onClick={() => setSelectedUnitForUpgrade(selectedUnitForUpgrade?.id === unit.id ? null : unit)}
                      className={`px-3 py-1.5 rounded-lg font-semibold text-sm transition-all ${
                        selectedUnitForUpgrade?.id === unit.id 
                          ? 'bg-blue-500 text-white ring-2 ring-blue-300' 
                          : unit.status === 'available'
                          ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                          : unit.status === 'blocked'
                          ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                          : 'bg-red-100 text-red-700 hover:bg-red-200'
                      }`}
                    >
                      {unit.unit_number}
                    </button>
                  ))}
                </div>
                
                {/* Selected Unit Details */}
                {selectedUnitForUpgrade && floorGroups[floor].find(u => u.id === selectedUnitForUpgrade.id) && (
                  <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg animate-in slide-in-from-top-2">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="text-lg font-bold text-blue-900">Flat {selectedUnitForUpgrade.unit_number}</h4>
                        <Badge className={
                          selectedUnitForUpgrade.status === 'available' ? 'bg-emerald-500' :
                          selectedUnitForUpgrade.status === 'blocked' ? 'bg-amber-500' : 'bg-red-500'
                        }>
                          {selectedUnitForUpgrade.status}
                        </Badge>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => setSelectedUnitForUpgrade(null)}>
                        <XCircle className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div className="p-2 bg-white rounded">
                        <p className="text-muted-foreground text-xs">Type</p>
                        <p className="font-semibold">{selectedUnitForUpgrade.bhk_type || '-'}</p>
                      </div>
                      <div className="p-2 bg-white rounded">
                        <p className="text-muted-foreground text-xs">Carpet Area</p>
                        <p className="font-semibold">{selectedUnitForUpgrade.carpet_area?.toLocaleString() || '-'} sq.ft</p>
                      </div>
                      <div className="p-2 bg-white rounded">
                        <p className="text-muted-foreground text-xs">Saleable Area</p>
                        <p className="font-semibold">{selectedUnitForUpgrade.saleable_area?.toLocaleString() || '-'} sq.ft</p>
                      </div>
                      <div className="p-2 bg-white rounded">
                        <p className="text-muted-foreground text-xs">Facing</p>
                        <p className="font-semibold capitalize">{selectedUnitForUpgrade.door_facing?.replace('_', '-') || '-'}</p>
                      </div>
                    </div>
                    
                    <div className="mt-3 p-3 bg-white rounded-lg">
                      <p className="text-muted-foreground text-xs mb-1">Price</p>
                      <p className="text-2xl font-bold text-green-600">{formatPrice(selectedUnitForUpgrade.price)}</p>
                    </div>
                    
                    {/* Other flats on same floor */}
                    {showUpgradeOptions && getUpgradeOptions(selectedUnitForUpgrade).length > 0 && (
                      <div className="mt-3 pt-3 border-t border-blue-200">
                        <p className="text-xs text-blue-600 mb-2 flex items-center gap-1">
                          <ArrowUpRight className="h-3 w-3" />
                          Other available options on Floor {floor}:
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {getUpgradeOptions(selectedUnitForUpgrade).map(u => (
                            <button
                              key={u.id}
                              onClick={() => setSelectedUnitForUpgrade(u)}
                              className="px-2 py-1 text-xs bg-white rounded border hover:bg-emerald-50 transition-colors"
                            >
                              {u.unit_number} • {u.bhk_type} • {formatPrice(u.price)}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : null}

      {/* Legend */}
      <Card>
        <CardContent className="p-4">
          <p className="text-sm font-medium mb-3">Legend</p>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded border-2 border-emerald-400 bg-emerald-50"></div>
              <span className="text-sm">Available</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded border-2 border-amber-400 bg-amber-50"></div>
              <span className="text-sm">Blocked / On Hold</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded border-2 border-red-400 bg-red-50"></div>
              <span className="text-sm">Sold</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default InventoryPage;
